import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { Shield, Loader2, Key, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function TwoFactorPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuthStore();

    // Get temp_token and email from navigation state
    const { tempToken, email } = location.state || {};

    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [showRecovery, setShowRecovery] = useState(false);
    const [recoveryCode, setRecoveryCode] = useState('');
    const [error, setError] = useState('');

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Redirect if no temp token
    useEffect(() => {
        if (!tempToken) {
            navigate('/login');
        }
    }, [tempToken, navigate]);

    const verifyMutation = useMutation({
        mutationFn: () => api.verify2FA(tempToken, code.join('')),
        onSuccess: (data) => {
            if (data.user && data.workspace && data.access_token) {
                login(data.user, data.workspace, data.access_token);
                navigate('/dashboard');
            }
        },
        onError: (err: Error) => {
            setError(err.message);
            // Clear code on error
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        },
    });

    const recoveryMutation = useMutation({
        mutationFn: () => api.useRecoveryCode(tempToken, recoveryCode),
        onSuccess: (data) => {
            if (data.user && data.workspace && data.access_token) {
                login(data.user, data.workspace, data.access_token);
                navigate('/dashboard');
            }
        },
        onError: (err: Error) => {
            setError(err.message);
        },
    });

    const handleCodeChange = (index: number, value: string) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when complete
        if (value && index === 5 && newCode.every(c => c !== '')) {
            verifyMutation.mutate();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newCode = [...code];
        for (let i = 0; i < pastedData.length; i++) {
            newCode[i] = pastedData[i];
        }
        setCode(newCode);
        if (pastedData.length === 6) {
            verifyMutation.mutate();
        }
    };

    const handleRecoverySubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (recoveryCode.length >= 5) {
            recoveryMutation.mutate();
        }
    };

    if (!tempToken) {
        return null;
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 px-4">
            <div className="w-full max-w-md">
                <div className="card p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Shield className="w-8 h-8 text-primary-600" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2">
                            {t('twoFactor.title', 'Two-Factor Authentication')}
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400">
                            {showRecovery
                                ? t('twoFactor.enterRecoveryCode', 'Enter one of your recovery codes')
                                : t('twoFactor.subtitle', 'Enter the 6-digit code from your authenticator app')
                            }
                        </p>
                        {email && (
                            <p className="text-sm text-neutral-500 mt-2">{email}</p>
                        )}
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-6 p-3 bg-danger-50 dark:bg-danger-600/20 border border-danger-200 dark:border-danger-500/30 rounded-lg flex items-center gap-2 text-danger-600">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {showRecovery ? (
                        /* Recovery Code Form */
                        <form onSubmit={handleRecoverySubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {t('twoFactor.recoveryCode', 'Recovery Code')}
                                </label>
                                <input
                                    type="text"
                                    value={recoveryCode}
                                    onChange={(e) => {
                                        setRecoveryCode(e.target.value.toUpperCase());
                                        setError('');
                                    }}
                                    className="input text-center text-lg tracking-widest"
                                    placeholder="XXXXX-XXXXX"
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={recoveryMutation.isPending || recoveryCode.length < 5}
                                className="btn-primary w-full"
                            >
                                {recoveryMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    t('twoFactor.verifyButton', 'Verify')
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setShowRecovery(false);
                                    setError('');
                                }}
                                className="btn-ghost w-full"
                            >
                                {t('twoFactor.useAuthenticator', 'Use authenticator app instead')}
                            </button>
                        </form>
                    ) : (
                        /* TOTP Code Form */
                        <div className="space-y-6">
                            {/* 6-digit code input */}
                            <div className="flex justify-center gap-2" onPaste={handlePaste}>
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleCodeChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className="w-12 h-14 text-center text-2xl font-bold border border-neutral-300 dark:border-neutral-600 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 outline-none transition-all bg-white dark:bg-neutral-700"
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            {/* Verify button */}
                            <button
                                onClick={() => verifyMutation.mutate()}
                                disabled={verifyMutation.isPending || code.some(c => !c)}
                                className="btn-primary w-full"
                            >
                                {verifyMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    t('twoFactor.verifyButton', 'Verify')
                                )}
                            </button>

                            {/* Recovery code link */}
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRecovery(true);
                                    setError('');
                                }}
                                className="btn-ghost w-full flex items-center justify-center gap-2"
                            >
                                <Key className="w-4 h-4" />
                                {t('twoFactor.useRecoveryCode', 'Use recovery code instead')}
                            </button>
                        </div>
                    )}

                    {/* Back to login & Lost access */}
                    <div className="mt-6 text-center space-y-2">
                        <button
                            onClick={() => navigate('/login')}
                            className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                        >
                            {t('twoFactor.backToLogin', '← Back to login')}
                        </button>
                        <div>
                            <button
                                onClick={() => navigate('/2fa-reset')}
                                className="text-xs text-danger-500 hover:text-danger-600"
                            >
                                {t('twoFactor.lostAccess', 'Lost access to authenticator?')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
